import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { authApi } from '../api/endpoints';
import { ApiError } from '../api/http';
import type { User } from '../api/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const ME_KEY = ['auth', 'me'];

/** Drops cached data of the previous session, but keeps the session query observed by this provider. */
const resetCache = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const login = useCallback(async (email: string, password: string) => {
    const session = await authApi.login(email, password);
    resetCache(queryClient);
    queryClient.setQueryData(ME_KEY, session.user);
  }, [queryClient]);

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => undefined);
    resetCache(queryClient);
    queryClient.setQueryData(ME_KEY, null);
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ user: me.data ?? null, loading: me.isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return value;
}

/** Returns the signed-in user; only call below a route that requires authentication. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('Utilisateur non connecté');
  return user;
}
